"use client";

import { ColumnDef, Row } from "@tanstack/react-table";
import { FileText } from "lucide-react";

import {
  useProgressNoteColumnFilters,
  type ProgressNoteWithClassification,
} from "@/src/components/cards/progress-note-card/progress-note-columns";
import { ProgressNoteToolbar } from "@/src/components/cards/progress-note-card/progress-note-toolbar";
import { CopyableContent } from "@/src/components/copyable-content";
import { DataTable } from "@/src/components/tables/data-table";
import { DataTableColumnHeader } from "@/src/components/tables/data-table-column-header";
import { ValerieCard } from "@/src/components/valerie-card";
import { EnabledFields } from "@/src/lib/schema-types";
import { getClassificationDisplay } from "@/src/lib/utils/classification";
import { formatDate } from "@/src/lib/utils/date-and-phone-utils";
import { isFieldEnabled, hasValue } from "@/src/lib/utils/object-utils";
import { Classification } from "@/src/models/constants/classifications";
import { formatICD10CodeForDisplay } from "@/src/models/icd10";

interface ProgressNoteTableProps {
  progressNotes: ProgressNoteWithClassification[];
  enabledFields?: EnabledFields;
}

export function ProgressNoteTable({ progressNotes, enabledFields }: ProgressNoteTableProps) {
  // Get column filters and map the namespaced parameter to the column name
  const [columnFilters] = useProgressNoteColumnFilters();
  const mappedColumnFilters = {
    classification: columnFilters.progressNoteClassification,
  };
  // Data is already in the correct format
  const dataWithClassification: ProgressNoteWithClassification[] = progressNotes;

  const CellWrapper = ({ children }: { children: React.ReactNode }) => {
    return <div className="w-full p-2">{children}</div>;
  };

  const allColumns: ColumnDef<ProgressNoteWithClassification>[] = [
    {
      accessorKey: "classification",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Classification" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("classification") as Classification;
        const displayValue = getClassificationDisplay(value);

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
    },
    {
      accessorKey: "dateOfProgressNote",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Note Date" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("dateOfProgressNote") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "progressNoteType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("progressNoteType") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
    },
    {
      accessorKey: "provider",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Provider" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const provider = row.getValue("provider") as any;
        const displayValue =
          provider && provider.givenName && provider.familyName ? `${provider.givenName} ${provider.familyName}` : "—";
        const npiValue = provider?.npiNumber || null;

        return (
          <CellWrapper>
            <div className="max-w-[500px] truncate font-medium no-select">
              <div>
                <CopyableContent text={displayValue} />
              </div>
              {npiValue && (
                <div className="text-sm text-muted-foreground mt-1">
                  NPI: <CopyableContent text={npiValue} />
                </div>
              )}
            </div>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
      accessorFn: (row) => {
        const provider = row.provider;

        return provider;
      },
    },
    {
      accessorKey: "specialty",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Specialty" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("specialty") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "location",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Location" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const location = row.getValue("location") as any;
        const displayValue = location && location.name ? location.name : "—";

        return (
          <CellWrapper>
            <span className="max-w-[500px] truncate font-medium no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "specialtyFolder",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Specialty Folder" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("specialtyFolder") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "surgeryName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Surgery" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("surgeryName") as string;

        return (
          <CellWrapper>
            <span className="max-w-[500px] truncate font-medium no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "hospitalAbbrev",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Hospital" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("hospitalAbbrev") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "orderNumber",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Order Number" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("orderNumber") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "signatureRequired",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Signature" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("signatureRequired") as boolean;
        const displayValue = value !== null && value !== undefined ? (value ? "Yes" : "No") : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
      filterFn: (row, id, value) => {
        const rowValue = row.getValue(id) as boolean;

        return value.includes(rowValue ? "true" : "false");
      },
    },
    {
      accessorKey: "flowSheetType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Flow Sheet" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("flowSheetType") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateOfEvent",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Event Date" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("dateOfEvent") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateOfDocument",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Document Date" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("dateOfDocument") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateReceived",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Received Date" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("dateReceived") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateSigned",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Signed Date" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const value = row.getValue("dateSigned") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "diagnosisList",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Diagnoses" />,
      cell: ({ row }: { row: Row<ProgressNoteWithClassification> }) => {
        const diagnoses = row.getValue("diagnosisList") as any[] | undefined;

        if (!diagnoses || diagnoses.length === 0) {
          return (
            <CellWrapper>
              <span className="no-select">—</span>
            </CellWrapper>
          );
        }

        return (
          <CellWrapper>
            <div className="max-w-[500px] no-select">
              <ul className="list-disc list-inside space-y-1">
                {diagnoses.map((diagnosis: any, index: number) => (
                  <li key={index} className="text-sm">
                    <CopyableContent
                      text={`${formatICD10CodeForDisplay(diagnosis.diagnosisCode)} - ${diagnosis.diagnosisText}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
    },
  ];

  // Filter columns to only show enabled fields that have data
  const columns = allColumns.filter((column) => {
    const fieldName = "accessorKey" in column && typeof column.accessorKey === "string" ? column.accessorKey : "";

    // Always show classification column, check others against enabled fields
    if (fieldName === "classification") return true;

    return (
      isFieldEnabled(enabledFields, fieldName) &&
      dataWithClassification.some((item) => hasValue(item[fieldName as keyof ProgressNoteWithClassification]))
    );
  });

  return (
    <ValerieCard className="w-full" icon={<FileText />} title="Progress Notes">
      <div className="rounded-lg overflow-hidden border">
        <DataTable
          columns={columns}
          data={dataWithClassification}
          columnFilters={mappedColumnFilters}
          ToolbarComponent={ProgressNoteToolbar}
          getRowUrl={() => ""}
        />
      </div>
    </ValerieCard>
  );
}
